import * as React from 'react';
import { Row, Col } from 'antd';
import { message, Modal, Typography, Alert } from 'src/design-system/components/atoms';
import withStyles, { WithStylesProps } from 'react-jss';
import { LinkOutlined, DownloadOutlined, CloseOutlined } from '@ant-design/icons';

const { Text } = Typography;
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import lodash from 'lodash';
import { sanitizeUrl } from '@utils/urlSanitizer';
import { ThemeType } from '@theming/jssTypes';

const styles = (theme: ThemeType) => ({
  grayCircle: {
    width: '32px',
    height: '32px',
    fontSize: theme.typography.fontSize.md,
    backgroundColor: theme.color.semantic.background.neutral.subtle,
    borderRadius: theme.radius.full,
    marginRight: theme.spacing[5],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

interface IDocumentTypeProps extends WithStylesProps<typeof styles> {
  theme: ThemeType;
  documentType: string;
  documentUrls: string[];
  index: number;
  availableDocumentTypesList: { id: string; name: string }[];
}

const DocumentType = (props: IDocumentTypeProps) => {
  const {
    documentType,
    documentUrls = [],
    index,
    classes,
    availableDocumentTypesList,
    theme,
  } = props;
  const rows = [];
  for (let i = 0; i < documentUrls.length; i += 3) {
    const rowData = documentUrls.slice(i, i + 3);
    rows.push(rowData);
  }

  const documentTypePrettyNameMapping = {};
  for (const data of availableDocumentTypesList) {
    documentTypePrettyNameMapping[data.id] = data.name;
  }
  return (
    <div style={{ marginTop: theme.spacing[2] }}>
      <div style={{ display: 'flex', marginTop: theme.spacing[2], alignItems: 'center' }}>
        <div className={classes.grayCircle}>{index + 1}</div>
        <div style={{ color: theme.color.semantic.text.primary, fontSize: theme.typography.fontSize.sm }}>
          {documentTypePrettyNameMapping[documentType] || documentType}
        </div>
      </div>
      <>
        {rows.map((row, index) => (
          <Row
            key={index}
            gutter={[16, 16]}
            style={{ marginTop: theme.spacing[2], marginLeft: `calc(${theme.spacing[8]} + ${theme.spacing[2]})` }}
          >
            {row.map((item, i) => {
              const { isValid, sanitizedUrl } = sanitizeUrl(item);
              
              return (
                <Col key={i} span={8}>
                  <div key={i}>
                    {!isValid ? (
                      <Alert message="This file may be invalid or inaccessible" type="error" showIcon />
                    ) : (
                      <a href={sanitizedUrl} target="_blank" rel="noopener noreferrer">
                        <>
                          <LinkOutlined style={{ color: theme.color.semantic.text.tertiary }} />
                          <Text
                            style={{
                              marginLeft: theme.spacing[2],
                              marginRight: theme.spacing[2],
                              maxWidth: '80%',
                              textOverflow: 'ellipsis',
                              color: theme.color.semantic.brand.default.secondary,
                            }}
                            ellipsis={{ tooltip: 'Click to Open' }}
                          >
                            {sanitizedUrl}
                          </Text>
                        </>
                      </a>
                    )}
                  </div>
                </Col>
              );
            })}
          </Row>
        ))}
      </>
    </div>
  );
};

const DocumentDetailsModal = (props: any) => {
  const handleModalClose = () => {
    props.onModalClose(false);
  };

  const { isVisible, documentDetails = [], classes, theme } = props;

  const documentDetailsByType = lodash.groupBy(documentDetails, 'type');
  const handleAllDocumentDetailsDownload = async () => {
    const documentUrls = documentDetails.map((item) => item.url);
    let invalidCount = 0;
    let failedCount = 0;
    let successCount = 0;
    
    const promises = documentUrls.map(async (url) => {
      const { isValid, sanitizedUrl } = sanitizeUrl(url);
      if (!isValid) {
        invalidCount++;
        return;
      }
      
      try {
        const response = await fetch(sanitizedUrl);
        const blob = await response.blob();
        const link = document.createElement('a');
        const downloadUrl = window.URL.createObjectURL(blob);
        link.href = downloadUrl;
        link.download = '';
        link.click();
        window.URL.revokeObjectURL(downloadUrl);
        successCount++;
      } catch (err) {
        failedCount++;
      }
    });
    await Promise.all(promises);
    
    // Show user-friendly messages
    if (invalidCount > 0) {
      message.warning(`${invalidCount} invalid URL(s) skipped`);
    }
    if (failedCount > 0) {
      message.error(`${failedCount} file(s) failed to download`);
    }
    if (successCount > 0 && invalidCount === 0 && failedCount === 0) {
      message.success(`Successfully downloaded ${successCount} file(s)`);
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ fontSize: theme.typography.fontSize.lg }}>Documents</div>
          <div style={{ flexGrow: 1 }}></div>
          <div onClick={handleAllDocumentDetailsDownload} style={{ marginRight: theme.spacing[5], display: 'flex', alignItems: 'center', cursor: 'pointer', color: theme.color.semantic.brand.default.secondary }}>
            <DownloadOutlined
              style={{ color: theme.color.semantic.brand.default.secondary, marginRight: theme.spacing[1] }}
            />
            <div>Download All</div>
          </div>
          <div>
          <CloseOutlined style={{ fontSize: theme.typography.fontSize.lg }} onClick={handleModalClose}/>
          </div>
        </div>
      }
      visible={isVisible}
      onCancel={handleModalClose}
      width={'50%'}
      footer={null}
      centered
      closable={false}
    >
      {Object.keys(documentDetailsByType).map((key: string, index: number) => {
        const documentUrls = documentDetailsByType[key]?.map((item) => item.url) || [];
        return (
          <DocumentType
            theme={theme}
            documentType={key}
            documentUrls={documentUrls}
            index={index}
            classes={classes}
            availableDocumentTypesList={props.availableDocumentTypesList}
          />
        );
      })}
    </Modal>
  );
};

const mapStateToProps = ({ masterData }, ownProps) => {
  return {
    availableDocumentTypesList: masterData.available_document_types || [],
  };
};

const DocumentDetailsModalStyled = withStyles(styles, { injectTheme: true })(
  DocumentDetailsModal,
);
const DocumentDetailsModalConnected = connect(mapStateToProps)(
  DocumentDetailsModalStyled,
);
export default withRouter(
  DocumentDetailsModalConnected,
) as React.ComponentType<any>;
